import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getReferenceString } from "@medplum/core";
import { Bundle, Communication, Patient, Provenance } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile, useSubscription } from "@medplum/react-hooks";

import { CommunicationInbox, compareInboxByLatestMessage } from "@/store/slices/inbox";
import { getPracticeIdForPatient, getResourcesFromBundle } from "@/utils/medplum";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import {
  fetchCommunicationInboxList,
  fetchCommunicationInboxListForSubscription,
  InboxListOptions,
} from "../services/inbox";
import { ClientInbox } from "../types/client-inbox";
import { applyReadReceipts, getReadCommunicationIds, upsertInboxThreads } from "../utils/inbox";
import { readProvenanceSubscription, threadReplyCriteria } from "../utils/subscriptions";

import useLocalChangeJournal from "./useLocalChangeJournal";

const SUBSCRIPTION_OPTIONS = { onError: (error: Error) => console.error(error) };
// The chart's rows show the last message under the topic, on loads and on reply refreshes alike.
const CHART_LIST_OPTIONS: InboxListOptions = { includeLastMessagePreview: true };
const NO_THREADS: CommunicationInbox[] = [];

type ThreadChanges = Partial<Omit<CommunicationInbox, "id">>;

interface LoadedInbox {
  /** Practice and client the threads were loaded for. */
  scopeKey: string;
  threads: CommunicationInbox[];
  hasError: boolean;
}

/**
 * Loads the threads the current user shares with one client and keeps them live. The state is
 * local to the chart, so the global inbox in the store is left alone.
 *
 * @param client - the chart's client. Nothing loads until it has an id.
 * @param isEnabled - false skips every fetch and subscription.
 * @returns the client's threads, their total unread count, and the loading and error states.
 */
export default function useClientInbox(
  client: Patient | undefined,
  isEnabled: boolean,
): ClientInbox {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [loadedInbox, setLoadedInbox] = useState<LoadedInbox>();
  // Each load gets an id, so a slow response for an earlier client never overwrites a newer one.
  // Subscription reloads share the counter, which an effect-local cancel flag would not cover.
  const latestLoadId = useRef(0);
  // The last load that wrote its result. While it trails latestLoadId a load is still running.
  const settledLoadId = useRef(0);
  // Whether the last load that wrote its result failed. A reply can't patch a list that never loaded.
  const lastLoadFailed = useRef(false);
  // Local changes to threads: read receipts, status changes and sent messages. A load or reply
  // refresh that started before a change applies it again when it lands, so its older copy of the
  // thread can't undo the change.
  const {
    record: recordLocalChange,
    beginFetch,
    endFetch,
    applyChangesSince,
  } = useLocalChangeJournal<CommunicationInbox>();

  const clientReference = isEnabled && client?.id ? getReferenceString(client) : undefined;
  // The client's own practice scopes the search, so the chart never follows the active practice
  // that another tab can change.
  const practiceId = useMemo(
    () => (clientReference ? getPracticeIdForPatient(client) : undefined),
    [client, clientReference],
  );
  const scopeKey = clientReference
    ? `${practiceId ?? "no-practice"}|${clientReference}`
    : undefined;
  const profileReference = profile ? getReferenceString(profile) : undefined;
  const subscriptions =
    practiceId && clientReference && profileReference
      ? {
          replyCriteria: threadReplyCriteria(profileReference, [profileReference, clientReference]),
          readReceipts: readProvenanceSubscription(profileReference),
        }
      : undefined;

  /** Fetches the client's threads. Only the latest request may write its result to state. */
  const reload = useCallback(async () => {
    if (!scopeKey || !clientReference) return;

    const loadId = ++latestLoadId.current;
    if (!practiceId) {
      console.error(`No practice found for ${clientReference}`);
      captureExceptionWithoutPhi(
        "Client has no practice to load the inbox for",
        new Error("Missing practice"),
        { clientId: client?.id },
        "warning",
      );
      settledLoadId.current = loadId;
      lastLoadFailed.current = true;
      setLoadedInbox({ scopeKey, threads: [], hasError: true });
      return;
    }

    const startSequence = beginFetch();
    try {
      medplum.invalidateSearches("Communication");
      const fetchedThreads = await fetchCommunicationInboxList(
        medplum,
        practiceId,
        clientReference,
        CHART_LIST_OPTIONS,
      );
      const threads = applyChangesSince(fetchedThreads, startSequence).sort(
        compareInboxByLatestMessage,
      );
      if (loadId === latestLoadId.current) {
        settledLoadId.current = loadId;
        lastLoadFailed.current = false;
        setLoadedInbox({ scopeKey, threads, hasError: false });
      }
    } catch (error) {
      captureExceptionWithoutPhi("Failed to load the client inbox", error);
      console.error(`Failed to load the inbox for ${clientReference}`);
      if (loadId === latestLoadId.current) {
        settledLoadId.current = loadId;
        lastLoadFailed.current = true;
        setLoadedInbox({ scopeKey, threads: [], hasError: true });
      }
    } finally {
      endFetch(startSequence);
    }
  }, [
    medplum,
    scopeKey,
    practiceId,
    clientReference,
    client?.id,
    beginFetch,
    endFetch,
    applyChangesSince,
  ]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * Fetches again only the threads that got new messages. While a load is still running it may have
   * counted those threads before the reply, so a newer full load replaces it instead. After a failed
   * load there is no list to patch, so a reply loads everything again.
   */
  const handleThreadReplies = useCallback(
    async (bundle: Bundle) => {
      const communications = getResourcesFromBundle("Communication", bundle) as Communication[];
      if (!scopeKey || !practiceId || !communications.length) return;

      if (
        latestLoadId.current === 0 ||
        settledLoadId.current !== latestLoadId.current ||
        lastLoadFailed.current
      ) {
        await reload();
        return;
      }

      const startSequence = beginFetch();
      try {
        medplum.invalidateSearches("Communication");
        const fetchedThreads = await fetchCommunicationInboxListForSubscription(
          medplum,
          communications,
          practiceId,
          CHART_LIST_OPTIONS,
        );
        const updatedThreads = applyChangesSince(fetchedThreads, startSequence);
        setLoadedInbox((current) =>
          current?.scopeKey === scopeKey && !current.hasError
            ? { ...current, threads: upsertInboxThreads(current.threads, updatedThreads) }
            : current,
        );
      } catch (error) {
        captureExceptionWithoutPhi("Failed to refresh the client inbox after a reply", error);
      } finally {
        endFetch(startSequence);
      }
    },
    [medplum, scopeKey, practiceId, reload, beginFetch, endFetch, applyChangesSince],
  );

  /** Clears the unread count of the threads the current user just read. */
  const handleReadReceipts = useCallback(
    (bundle: Bundle) => {
      const provenances = getResourcesFromBundle("Provenance", bundle) as Provenance[];
      for (const threadId of getReadCommunicationIds(provenances)) {
        recordLocalChange(threadId, { unread: 0 });
      }
      setLoadedInbox((current) => {
        if (!current) return current;

        const threads = applyReadReceipts(current.threads, provenances);
        return threads === current.threads ? current : { ...current, threads };
      });
    },
    [recordLocalChange],
  );

  /** Applies local changes to one thread and re-sorts the list by latest message. */
  const updateThread = useCallback(
    (threadId: string, changes: ThreadChanges) => {
      recordLocalChange(threadId, changes);
      setLoadedInbox((current) => {
        if (!current) return current;

        const threads = current.threads
          .map((thread) => (thread.id === threadId ? { ...thread, ...changes } : thread))
          .sort(compareInboxByLatestMessage);
        return { ...current, threads };
      });
    },
    [recordLocalChange],
  );

  /**
   * Adds a thread started from the chart, keeping the list ordered by latest message. The reload
   * that follows the insert supersedes any earlier in-flight load.
   */
  const addThread = useCallback(
    (thread: CommunicationInbox) => {
      setLoadedInbox((current) =>
        current ? { ...current, threads: upsertInboxThreads(current.threads, [thread]) } : current,
      );
      reload();
    },
    [reload],
  );

  // Derived from the current scope, so the previous client's threads never paint for a new one.
  const isCurrent = !!scopeKey && loadedInbox?.scopeKey === scopeKey;
  const threads = isCurrent ? loadedInbox.threads : NO_THREADS;
  const isLoading = isEnabled && !isCurrent;
  const hasError = isCurrent && loadedInbox.hasError;

  useSubscription(subscriptions?.replyCriteria, handleThreadReplies, SUBSCRIPTION_OPTIONS);

  useSubscription(subscriptions?.readReceipts.criteria, handleReadReceipts, {
    ...SUBSCRIPTION_OPTIONS,
    subscriptionProps: subscriptions?.readReceipts.subscriptionProps,
  });

  return useMemo(
    () => ({
      threads,
      isLoading,
      hasError,
      unreadCount: threads.reduce((total, thread) => total + thread.unread, 0),
      updateThread,
      addThread,
    }),
    [threads, isLoading, hasError, updateThread, addThread],
  );
}
